/**
 * 金额 / 日期格式化工具（SPEC-002 §11.8 「priceCents 单位约定」）。
 *
 * 币种 → 小数位映射：
 *   USDT → 6（micro-USDT，1 USDT = 1_000_000 priceCents）
 *   KHR  → 0（柬埔寨瑞尔无小数）
 *   USD  → 2
 *   HKD  → 2
 *
 * 后端 BigInt 金额通过 JSON 序列化为字符串，这里统一接受 `bigint | number | string`。
 */

export type CurrencyCode = 'USDT' | 'KHR' | 'USD' | 'HKD';

const DECIMALS: Record<CurrencyCode, number> = {
  USDT: 6,
  KHR: 0,
  USD: 2,
  HKD: 2,
};

function decimalsFor(currency: string): number {
  return DECIMALS[(currency?.toUpperCase() as CurrencyCode) ?? 'USDT'] ?? 2;
}

function toBigInt(value: bigint | number | string): bigint {
  if (typeof value === 'bigint') return value;
  if (typeof value === 'number') return BigInt(Math.trunc(value));
  // string path: BigInt() throws on malformed → bubble up
  return BigInt(value);
}

/**
 * 将 `cents` 转成人类可读的数字字符串（用于 UI 显示）。
 * - USDT 默认显示 2 位小数（避免 6 位小数拥挤），内部计算仍按 6 位精度。
 * - KHR 无小数，千分位显示。
 * - USD / HKD 按 2 位小数显示。
 *
 * @example formatAmount('1500000', 'USDT')          // "1.50"
 * @example formatAmount('12000', 'KHR', 'zh-CN')    // "12,000"
 * @example formatAmount('199', 'USD')               // "1.99"
 */
export function formatAmount(
  cents: bigint | number | string,
  currency: string,
  locale: string = 'en-US',
): string {
  const dec = decimalsFor(currency);
  const raw = toBigInt(cents);
  const divisor = 10n ** BigInt(dec);
  const whole = raw / divisor;
  const fraction = raw % divisor;

  // Display fractional precision (separate from internal precision).
  const displayDecimals = dec >= 2 ? 2 : dec;

  if (displayDecimals === 0) {
    return new Intl.NumberFormat(locale).format(Number(whole));
  }

  // Pad fraction to full decimals, then slice to displayDecimals.
  const fractionStr = fraction.toString().padStart(dec, '0').slice(0, displayDecimals);
  const wholeStr = new Intl.NumberFormat(locale).format(Number(whole));
  return `${wholeStr}.${fractionStr}`;
}

/** 币种符号映射（简化版；非金融结算用途）。 */
const SYMBOL: Record<string, string> = {
  USDT: 'USDT',
  KHR: '៛',
  USD: '$',
  HKD: 'HK$',
};

/**
 * 带币种符号的金额字符串。USDT / KHR 这种非标准 ISO 货币用后缀/前缀符号手绘。
 *
 * @example formatPrice('1500000', 'USDT')  // "1.50 USDT"
 * @example formatPrice('12000', 'KHR')     // "៛12,000"
 * @example formatPrice('199', 'USD')       // "$1.99"
 */
export function formatPrice(
  cents: bigint | number | string,
  currency: string,
  locale: string = 'en-US',
): string {
  const upper = currency?.toUpperCase() ?? 'USDT';
  const num = formatAmount(cents, upper, locale);
  const sym = SYMBOL[upper] ?? upper;
  // USDT 后缀；法币前缀
  if (upper === 'USDT') return `${num} ${sym}`;
  return `${sym}${num}`;
}

/** 把 ISO 字符串 / Date 转成本地化友好的时间串（Intl） */
export function formatDateTime(iso: string | Date | null | undefined, locale = 'en-US'): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}
